import { useWidgetContext } from 'widgets/WidgetContext';
import {
    isCancelledOrRejected,
    isChildOrder,
    isFilled,
    OpenSubmissionsDataState,
    OpenSubmissionsStreamItem,
} from 'widgets/openSubmissions/store/stream/OpenSubmissionsStreamDataState';
import { safeCalculatePercent } from 'utils/percent';
import { RowDataItem } from 'widgets/openSubmissions/grid/OpenSubmissionsGrid';
import { useDispatch } from 'store/hooks';
import { useCallback, useEffect } from 'react';
import {
    openSubmissionsFadeOutAction,
    OpenSubmissionStreamWidgetType,
    removeSubmissionAction,
} from 'widgets/openSubmissions/store/stream/actions';
import { usePrevious } from '@instinet-analytics/core-lib';

import styleConstants from './variables.module.scss';
import {
    OpenSubmissionsChartData,
    useOpenSubmissionsChartData,
} from 'widgets/openSubmissions/OpenSubmissionsChartData';
import orderBy from 'lodash/orderBy';
import uniqWith from 'lodash/uniqWith';

const flashDuration =
    parseFloat(styleConstants.flashColourAnimationDuration) *
    parseFloat(styleConstants.flashColourAnimationIterationCount) *
    1000;
const fadeDuration = parseFloat(styleConstants.fadingAnimationDuration) * 1000;

export interface OpenSubmissionsData {
    openSubmissionRows: RowDataItem[];
    chartData: OpenSubmissionsChartData;
}

export function useOpenSubmissionsData(
    streamState: OpenSubmissionsDataState | undefined,
    instantFeedback = false
): OpenSubmissionsData {
    const { widgetType, widgetKey } = useWidgetContext();

    const submissions = sanitise(streamState);
    const chartData = useOpenSubmissionsChartData(submissions);

    const openSubmissionsWidgetType =
        widgetType as OpenSubmissionStreamWidgetType;

    const childSubmissions = submissions.filter(isChildOrder);
    const rowData: RowDataItem[] = childSubmissions.map<RowDataItem>((i) => ({
        ...i,
        filled: safeCalculatePercent(i.executedQuantity, i.orderQty),
    }));

    const dispatch = useDispatch();
    const dispatchDelayedFadeout = useCallback(
        (item: RowDataItem) => {
            setTimeout(
                () => {
                    dispatch(
                        openSubmissionsFadeOutAction(
                            openSubmissionsWidgetType,
                            widgetKey,
                            item._i
                        )
                    );
                },
                instantFeedback ? 0 : flashDuration
            );
        },
        [openSubmissionsWidgetType, widgetKey, dispatch, instantFeedback]
    );
    const dispatchDelayedRemoveSubmission = useCallback(
        (item: RowDataItem) => {
            setTimeout(
                () => {
                    dispatch(
                        removeSubmissionAction(
                            openSubmissionsWidgetType,
                            widgetKey,
                            item._i
                        )
                    );
                },
                instantFeedback ? 0 : fadeDuration
            );
        },
        [openSubmissionsWidgetType, widgetKey, dispatch, instantFeedback]
    );

    const prevRowData = usePrevious(rowData);

    useEffect(() => {
        if (rowData.length === 0) {
            return;
        }
        for (const newItem of rowData) {
            const oldItem =
                prevRowData && prevRowData.find((r) => r._i === newItem._i);
            if (!oldItem) {
                continue;
            }

            if (newItem.shouldRemove !== oldItem.shouldRemove) {
                if (isCancelledOrRejected(newItem) || isFilled(newItem)) {
                    // action is delayed for duration of flashing animation
                    // before initiating fadeout
                    dispatchDelayedFadeout(newItem);
                } else {
                    // action is delayed for duration of fadeout animation
                    // before removing the submissions from the data.
                    // This specific case only occurs if the stream indicates
                    // to remove a submission that was neither cancelled nor filled.
                    dispatchDelayedRemoveSubmission(newItem);
                }
            }

            // 'isFading' is added to row data after flashing animation completed
            // ('dispatchDelayedFadeout' above was called)
            if (newItem.isFading !== oldItem.isFading) {
                // action is delayed for duration of fadeout animation
                // before removing the submissions from the data
                dispatchDelayedRemoveSubmission(newItem);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatchDelayedFadeout, dispatchDelayedRemoveSubmission, rowData]);

    return { openSubmissionRows: rowData, chartData };
}

function sanitise(
    streamState: OpenSubmissionsDataState | undefined
): OpenSubmissionsStreamItem[] {
    if (!streamState?.submissions) {
        return [];
    }

    const orderedByI = orderBy(streamState.submissions, ['_i'], ['desc']);
    return orderBy(
        uniqWith(orderedByI, (x, y) => x.id === y.id),
        ['_i'],
        ['asc']
    );
}
